import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import {func, number, object} from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {canChangePublicationStatus} from '../../auth.js';
import {
  useDataTypes,
  useKeywords,
  usePermissions,
  usePublicationTypes,
  useTopics,
  useUploadAttachment,
  useUploadImage,
} from '../../client.js';
import AuthorGroup from './AuthorGroup.jsx';
import ComboBox from './ComboBox.jsx';
import Form from './Form.jsx';
import GeometryInput from './GeometryInput.jsx';
import InputList from './InputList.jsx';
import LinkGroup from './LinkGroup.jsx';
import MultiSelect from './MultiSelect.jsx';
import TextField, {utcDateTransform} from './TextField.jsx';
import Toggle from './Toggle.jsx';

function historicalDateValidator(value) {
  return Date.parse(value) < Date.now() || 'Date must be in the past';
}

function HiddenLinkFields({link, linkIndex}) {
  const {setValue} = useFormContext();

  useEffect(() => {
    if (!link) {
      return;
    }
    for (const key of Object.keys(link)) {
      setValue(`links.${linkIndex}.${key}`, link[key]);
    }
  }, [link, linkIndex, setValue]);

  if (!link || !Object.keys(link).length) {
    return null;
  }

  return Object.keys(link).map(key => (
    <TextField
      key={key}
      name={`links.${linkIndex}.${key}`}
      label="hidden"
      type="hidden"
      sx={{display: 'none'}}
      defaultValue={link[key]}
    />
  ));
}

HiddenLinkFields.propTypes = {
  link: object,
  linkIndex: number.isRequired,
};

/**
 * Some detail about publication links.
 *
 * There are four link rel values that we care about:
 *
 *  - alternate: publicly accessible URL for the publication on the internet (one)
 *  - attachment: link to an internally stored PDF copy of the publication (zero or one)
 *  - image: link to an internally stored cover image for the publication (zero or one)
 *  - related: links to blog post, etc. that may be related to the publication (zero or more)
 *
 * The API will also return other links that we don't care about (e.g. self).  The order of the
 * links from the API is also not guaranteed.  When we create or edit a publication, we only include
 * the links that we care about, and the form input elements have a fixed order (this is important):
 *
 *  - 0: alternate
 *  - 1: attachment (even if it is empty)
 *  - 2: image (even if it is empty)
 *  - 3..n: related
 */

function PublicationForm({onSubmit, publication}) {
  const {data: keywords} = useKeywords();
  const {data: topics} = useTopics();
  const {data: dataTypes} = useDataTypes();
  const {data: publicationTypes} = usePublicationTypes();
  const [attachmentFile, setAttachmentFile] = useState();
  const [imageFile, setImageFile] = useState();
  const [showGeometry, setShowGeometry] = useState(!!publication?.geometry);
  const {
    request: uploadAttachment,
    data: uploadedAttachment,
    error: uploadAttachmentError,
    loading: uploadingAttachment,
  } = useUploadAttachment();
  const {
    request: uploadImage,
    data: uploadedImage,
    error: uploadImageError,
    loading: uploadingImage,
  } = useUploadImage();
  const {data: permissions} = usePermissions();

  const alternateLinks = [];
  const attachmentLinks = [];
  const imageLinks = [];
  const relatedLinks = [];
  if (publication) {
    for (const link of publication.links) {
      if (link.rel === 'alternate') {
        alternateLinks.push(link);
      } else if (link.rel === 'attachment') {
        attachmentLinks.push(link);
      } else if (link.rel === 'image') {
        imageLinks.push(link);
      } else if (link.rel === 'related') {
        relatedLinks.push(link);
      }
    }
  }

  const [attachmentLink, setAttachmentLink] = useState(
    attachmentLinks[0] || {},
  );

  const [imageLink, setImageLink] = useState(imageLinks[0] || {});

  const relatedLinkOffset = 3;

  const handleAttachmentFileChange = useCallback(
    event => {
      const attachment = event.target.files[0];
      if (attachment && attachment.type === 'application/pdf') {
        setAttachmentFile(attachment);
        uploadAttachment(attachment);
      }
    },
    [uploadAttachment],
  );

  const handleImageFileChange = useCallback(
    event => {
      const image = event.target.files[0];
      if (image && image.type.startsWith('image/')) {
        setImageFile(image);
        uploadImage(image, {'content-type': image.type});
      }
    },
    [uploadImage],
  );

  const handleGeometryAccordionToggle = useCallback(
    () => setShowGeometry(showGeometry => !showGeometry),
    [],
  );

  useEffect(() => {
    if (!attachmentFile || !uploadedAttachment) {
      return;
    }
    setAttachmentLink({
      title: attachmentFile.name,
      type: attachmentFile.type,
      rel: 'attachment',
      href: uploadedAttachment.id,
    });
  }, [attachmentFile, uploadedAttachment]);

  let attachmentStatus;
  if (!attachmentFile) {
    attachmentStatus = attachmentLink.href
      ? 'Replace attachment'
      : 'Upload attachment (PDF)';
  } else if (uploadingAttachment) {
    attachmentStatus = `Uploading ${attachmentFile.name}`;
  } else if (uploadAttachmentError) {
    attachmentStatus = `Error uploading ${attachmentFile.name}: ${uploadAttachmentError.message}`;
  } else {
    attachmentStatus = `Attached ${attachmentFile.name}`;
  }

  useEffect(() => {
    if (!imageFile || !uploadedImage) {
      return;
    }
    setImageLink({
      title: imageFile.name,
      type: imageFile.type,
      rel: 'image',
      href: uploadedImage.id,
    });
  }, [imageFile, uploadedImage]);

  let imageStatus;
  if (!imageFile) {
    imageStatus = imageLink.href ? 'Replace image' : 'Upload image';
  } else if (uploadingImage) {
    imageStatus = `Uploading ${imageFile.name}`;
  } else if (uploadImageError) {
    imageStatus = `Error uploading ${imageFile.name}: ${uploadImageError.message}`;
  } else {
    imageStatus = `Uploaded ${imageFile.name}`;
  }

  return (
    <Form onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid size={{xs: 12, sm: 6}}>
          <TextField
            name="title"
            label="Title"
            defaultValue={publication?.title}
            required
            fullWidth
            autoFocus
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6}}>
          <LinkGroup
            label="Link to publication"
            required
            requireTitle={false}
            rel="alternate"
            value={alternateLinks[0] || null}
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6}}>
          <TextField
            fullWidth
            name="publisher"
            label="Journal/Publication"
            defaultValue={publication?.publisher}
            required
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6}}>
          <TextField
            fullWidth
            name="datePublished"
            label="Date Published"
            defaultValue={publication?.datePublished}
            required
            type="date"
            transform={utcDateTransform}
            rules={{validate: historicalDateValidator}}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <InputList
            offset={0}
            list={publication?.authors}
            component={<AuthorGroup />}
            label="Author"
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <TextField
            name="abstract"
            label="Abstract"
            defaultValue={publication?.abstract}
            required
            multiline
            minRows={4}
            fullWidth
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <ComboBox
            name="type"
            label="Publication Type"
            inputLabel="Add publication type"
            possibleValues={publicationTypes || []}
            defaultValue={publication?.type}
            rules={{required: 'Publication type is required'}}
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <MultiSelect
            name="dataTypes"
            label="Data Types"
            inputLabel="Add data type"
            possibleValues={dataTypes}
            defaultValues={publication?.dataTypes}
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <MultiSelect
            name="topics"
            label="Topics"
            inputLabel="Add topic"
            possibleValues={topics}
            defaultValues={publication?.topics}
            rules={{required: 'At least one topic is required'}}
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <MultiSelect
            name="keywords"
            label="Keywords"
            inputLabel="Add keyword"
            possibleValues={keywords}
            defaultValues={publication?.keywords}
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <InputList
            offset={relatedLinkOffset}
            list={relatedLinks}
            component={<LinkGroup rel="related" />}
            label="Link"
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <Accordion
            variant="outlined"
            expanded={showGeometry}
            onChange={handleGeometryAccordionToggle}
          >
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
              <FormLabel>Publication Study Area</FormLabel>
            </AccordionSummary>
            <AccordionDetails sx={{height: 350, paddingTop: 0}}>
              <GeometryInput
                name="geometry"
                defaultValue={publication?.geometry}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid size={{xs: 12}}>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <input
              style={{display: 'none'}}
              id="upload-attachment-button"
              type="file"
              accept="application/pdf"
              disabled={!!attachmentFile}
              onChange={handleAttachmentFileChange}
            />
            <label htmlFor="upload-attachment-button">
              <Button variant="outlined" component="span">
                {attachmentStatus}
              </Button>
            </label>
          </Box>
        </Grid>

        <HiddenLinkFields link={attachmentLink} linkIndex={1} />

        <Grid size={{xs: 12}}>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <input
              style={{display: 'none'}}
              id="upload-image-button"
              type="file"
              accept="image/*"
              disabled={!!imageFile}
              onChange={handleImageFileChange}
            />
            <label htmlFor="upload-image-button">
              <Button variant="outlined" component="span">
                {imageStatus}
              </Button>
            </label>
          </Box>
        </Grid>
      </Grid>

      <HiddenLinkFields link={imageLink} linkIndex={2} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 2,
          marginBottom: 5,
        }}
      >
        {canChangePublicationStatus(permissions) && (
          <Toggle
            label="Published"
            name="published"
            defaultChecked={publication?.published}
          />
        )}
        <Toggle
          label="Featured"
          name="featured"
          defaultChecked={publication?.featured}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={uploadingAttachment || uploadingImage}
        >
          Save Publication
        </Button>
      </Box>
    </Form>
  );
}

PublicationForm.propTypes = {
  onSubmit: func.isRequired,
  publication: object,
};

export default PublicationForm;
